import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MembershipPlan, CreateMembershipPlanRequest } from '../../types/membership';

interface MembershipPlanFormProps {
  initialData?: MembershipPlan | null;
  onSubmit: (data: CreateMembershipPlanRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const MembershipPlanForm: React.FC<MembershipPlanFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateMembershipPlanRequest>({
    name: '',
    description: '',
    type: 'TIERED',
    price: 0,
    duration: 1,
    durationUnit: 'MONTHS',
    features: {},
  });

  const [newFeatureKey, setNewFeatureKey] = useState('');
  const [newFeatureValue, setNewFeatureValue] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description,
        type: initialData.type,
        price: initialData.price,
        duration: initialData.duration,
        durationUnit: initialData.durationUnit,
        features: initialData.features || {},
      });
    }
  }, [initialData]);

  const handleInputChange = (field: keyof CreateMembershipPlanRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'price' || field === 'duration' ? Number(value) : value,
    }));
  };

  const handleAddFeature = () => {
    if (newFeatureKey.trim() && newFeatureValue.trim()) {
      setFormData(prev => ({
        ...prev,
        features: {
          ...prev.features,
          [newFeatureKey.trim()]: newFeatureValue.trim(),
        },
      }));
      setNewFeatureKey('');
      setNewFeatureValue('');
    }
  };

  const handleRemoveFeature = (key: string) => {
    setFormData(prev => {
      const newFeatures = { ...prev.features };
      delete newFeatures[key];
      return {
        ...prev,
        features: newFeatures,
      };
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  const planTypeDescriptions = {
    TIERED: 'Different service levels with varying benefits',
    CREDIT_BASED: 'Pre-purchased credits for services',
    ALL_INCLUSIVE: 'Unlimited access to all services',
    PREPAID: 'Fixed package with specific services',
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Plan Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            required
            placeholder="e.g., Premium Membership"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Plan Type</InputLabel>
            <Select
              value={formData.type}
              onChange={handleInputChange('type')}
              label="Plan Type"
            >
              <MenuItem value="TIERED">Tiered</MenuItem>
              <MenuItem value="CREDIT_BASED">Credit Based</MenuItem>
              <MenuItem value="ALL_INCLUSIVE">All Inclusive</MenuItem>
              <MenuItem value="PREPAID">Prepaid</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {planTypeDescriptions[formData.type]}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            multiline
            rows={3}
            required
            placeholder="Describe the benefits and features of this membership plan"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={formData.price}
            onChange={handleInputChange('price')}
            required
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Duration"
            type="number"
            value={formData.duration}
            onChange={handleInputChange('duration')}
            required
            inputProps={{ min: 1 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth required>
            <InputLabel>Duration Unit</InputLabel>
            <Select
              value={formData.durationUnit}
              onChange={handleInputChange('durationUnit')}
              label="Duration Unit"
            >
              <MenuItem value="DAYS">Days</MenuItem>
              <MenuItem value="MONTHS">Months</MenuItem>
              <MenuItem value="YEARS">Years</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Features
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add specific features and benefits for this membership plan
              </Typography>

              {/* Existing Features */}
              {Object.entries(formData.features).map(([key, value]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1,
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: 120 }}>
                    {key}:
                  </Typography>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {String(value)}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFeature(key)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              {/* Add New Feature */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Feature Name"
                  value={newFeatureKey}
                  onChange={(e) => setNewFeatureKey(e.target.value)}
                  size="small"
                  placeholder="e.g., Monthly Services"
                />
                <TextField
                  label="Feature Value"
                  value={newFeatureValue}
                  onChange={(e) => setNewFeatureValue(e.target.value)}
                  size="small"
                  placeholder="e.g., 5 services per month"
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddFeature}
                  disabled={!newFeatureKey.trim() || !newFeatureValue.trim()}
                >
                  Add
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !formData.name || !formData.description || formData.price <= 0}
            >
              {isLoading ? 'Saving...' : initialData ? 'Update Plan' : 'Create Plan'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MembershipPlanForm;